import { HttpHeaders, HttpParams } from '@angular/common/http';
import {
  readVaultTenantIdFromAssignmentRow,
  readVaultTenantSnapshot,
  resolveVaultActiveTenantId,
  vaultTenantHeaders,
} from '@nebula/shared-vault/data-access';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';

/**
 * Misma fuente que `IngestProjectService.tenantHeaders`: `getContext().tenantId`
 * (trim, sin validar UUID). Si hay valor, es el tenant activo de la app.
 */
export function tenantIdFromServerContext(serverContext: ServerContextService): string {
  return (serverContext.getContext().tenantId || '').trim();
}

/**
 * Si `ServerContext` en memoria está vacío pero `localStorage` ya tiene tenant (p. ej. tras
 * `persistVaultTenantScopeToStorage` en Auth), rehidrata el subject sin recargar la página.
 */
export function syncServerContextFromLocalStorage(serverContext: ServerContextService): void {
  if (tenantIdFromServerContext(serverContext)) {
    return;
  }
  if (typeof localStorage === 'undefined') {
    return;
  }
  const fromLs = (
    localStorage.getItem('tenant_id') ||
    localStorage.getItem('tenantId') ||
    localStorage.getItem('selected_tenant_id') ||
    ''
  ).trim();
  if (fromLs) {
    persistActiveTenantId(fromLs, serverContext);
    return;
  }
  try {
    const raw = localStorage.getItem('vault_server_context');
    if (raw) {
      const parsed = JSON.parse(raw) as { tenantId?: string };
      const tid = (parsed.tenantId || '').trim();
      if (tid) {
        persistActiveTenantId(tid, serverContext);
      }
    }
  } catch {
    /* ignore */
  }
}

/** Primer tenant no vacío (asignar / UI; no exige solo UUID). */
export function firstNonEmptyTenantId(
  ...candidates: Array<string | null | undefined>
): string {
  for (const value of candidates) {
    const tid = (value || '').trim();
    if (tid) {
      return tid;
    }
  }
  return '';
}

/**
 * Tenant para APIs Vault — paridad Ingest (`ServerContext` primero, sin bloqueo extra en Asignar).
 * Retención, informes, repositorio y asignaciones deben usar esta función.
 */
export function resolveVaultApiTenantId(serverContext: ServerContextService): string {
  syncServerContextFromLocalStorage(serverContext);
  const fromContext = tenantIdFromServerContext(serverContext);
  if (fromContext) {
    return fromContext;
  }
  return resolveTenantId(serverContext);
}

export function normalizeTenantId(value: string | null | undefined): string {
  const tenantId = (value || '').trim();
  if (!tenantId) return '';
  const uuidV4ish = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidV4ish.test(tenantId) ? tenantId : '';
}

/**
 * Solo UUID válido. `companyId` Nebula (p. ej. `7` o `7:1`) ≠ `tenants.id` Datavault.
 * Enviar un id no UUID a `/api/file-assignments/assign` provoca 400/500 en API.
 */
export function coerceVaultScopeId(value: unknown): string {
  if (value == null) return '';
  return normalizeTenantId(String(value).trim());
}

function tenantFromCompanyOnRecord(record: Record<string, unknown> | undefined): string {
  if (!record) return '';
  for (const key of ['companyId', 'company_id']) {
    const id = coerceVaultScopeId(record[key]);
    if (id) return id;
  }
  const user = (record['User'] ?? record['user']) as Record<string, unknown> | undefined;
  if (user) {
    for (const key of ['companyId', 'company_id']) {
      const id = coerceVaultScopeId(user[key]);
      if (id) return id;
    }
  }
  return '';
}

/** `companyId` del JWT / `user_data` (flujo Nebula: login → empresa → Vault). */
export function tenantFromCompanyScope(): string {
  if (typeof localStorage === 'undefined') return '';
  const token = localStorage.getItem('access_token')?.trim();
  if (token) {
    const parsed = decodeJwtPayload(token);
    const fromJwt = tenantFromCompanyOnRecord(parsed ?? undefined);
    if (fromJwt) return fromJwt;
  }
  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      return tenantFromCompanyOnRecord(JSON.parse(raw) as Record<string, unknown>);
    }
  } catch {
    return '';
  }
  return '';
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function tenantFromAccessToken(): string {
  if (typeof localStorage === 'undefined') return '';
  const token = localStorage.getItem('access_token')?.trim();
  if (!token) return '';
  const parsed = decodeJwtPayload(token);
  if (!parsed) return '';

  const topLevel = [
    parsed['tenant_id'],
    parsed['tenantId'],
    parsed['active_tenant_id'],
    parsed['activeTenantId'],
  ].find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;
  if (topLevel) {
    return normalizeTenantId(topLevel) || topLevel.trim();
  }

  const user = (parsed['User'] ?? parsed['user']) as Record<string, unknown> | undefined;
  if (user) {
    for (const key of ['tenant_id', 'tenantId', 'active_tenant_id', 'activeTenantId']) {
      const v = user[key];
      if (typeof v === 'string' && v.trim()) {
        return normalizeTenantId(v) || v.trim();
      }
    }
    const tenants = user['tenants'];
    if (Array.isArray(tenants) && tenants.length > 0) {
      const first = tenants[0] as Record<string, unknown>;
      const tid = first['tenant_id'] ?? first['tenantId'] ?? first['id'];
      if (typeof tid === 'string' && tid.trim()) {
        return normalizeTenantId(tid) || tid.trim();
      }
    }
    const fromCompany = tenantFromCompanyOnRecord(user);
    if (fromCompany) return fromCompany;
  }
  return tenantFromCompanyOnRecord(parsed);
}

/**
 * Tenant activo para UI/filtros (legacy axios): localStorage sin exigir UUID,
 * luego contexto, JWT y `vault_server_context`.
 */
export function resolveActiveTenantScopeId(serverContext: ServerContextService): string {
  if (typeof localStorage !== 'undefined') {
    const raw = (
      localStorage.getItem('tenant_id') ||
      localStorage.getItem('tenantId') ||
      localStorage.getItem('selected_tenant_id') ||
      ''
    ).trim();
    if (raw) {
      return normalizeTenantId(raw) || raw;
    }
  }

  const fromResolved = resolveTenantId(serverContext);
  if (fromResolved) {
    return fromResolved;
  }

  const fromContext = tenantIdFromServerContext(serverContext);
  if (fromContext) {
    return fromContext;
  }

  return tenantFromAccessToken();
}

/** Tenant del perfil en `localStorage.user_data` (admin empresa sin selector global). */
export function tenantFromUserData(): string {
  if (typeof localStorage === 'undefined') {
    return '';
  }
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) {
      return '';
    }
    const data = JSON.parse(raw) as Record<string, unknown>;
    for (const key of ['tenant_id', 'tenantId', 'active_tenant_id', 'activeTenantId']) {
      const v = data[key];
      if (typeof v === 'string' && v.trim()) {
        return normalizeTenantId(v) || v.trim();
      }
    }
    const user = (data['User'] ?? data['user']) as Record<string, unknown> | undefined;
    if (user) {
      for (const key of ['tenant_id', 'tenantId', 'active_tenant_id', 'activeTenantId']) {
        const v = user[key];
        if (typeof v === 'string' && v.trim()) {
          return normalizeTenantId(v) || v.trim();
        }
      }
      const tenants = user['tenants'];
      if (Array.isArray(tenants) && tenants.length > 0) {
        const first = tenants[0] as Record<string, unknown>;
        const tid = first['tenant_id'] ?? first['tenantId'] ?? first['id'];
        if (typeof tid === 'string' && tid.trim()) {
          return normalizeTenantId(tid) || tid.trim();
        }
      }
    }
    const tenants = data['tenants'];
    if (Array.isArray(tenants) && tenants.length > 0) {
      const first = tenants[0] as Record<string, unknown>;
      const tid = first['tenant_id'] ?? first['tenantId'] ?? first['id'];
      if (typeof tid === 'string' && tid.trim()) {
        return normalizeTenantId(tid) || tid.trim();
      }
    }
    const fromCompany = tenantFromCompanyOnRecord(data);
    if (fromCompany) return fromCompany;
  } catch {
    return '';
  }
  return '';
}

/** Tenant activo para asignación: selector, sesión, contexto o JWT. */
export function resolveAssignTenantScopeId(serverContext: ServerContextService): string {
  return resolveActiveTenantScopeId(serverContext) || tenantFromUserData();
}

/** Persiste tenant activo (paridad legacy `localStorage.tenant_id` + contexto shell). */
export function persistActiveTenantId(
  tenantId: string,
  serverContext: ServerContextService,
): void {
  let id = (tenantId || '').trim();
  if (!id) {
    id = readVaultTenantSnapshot()?.tenant_id?.trim() || '';
  }
  if (typeof localStorage !== 'undefined') {
    if (id) {
      localStorage.setItem('tenant_id', id);
    } else {
      localStorage.removeItem('tenant_id');
    }
  }
  serverContext.setContext({ tenantId: id });
}

/**
 * Orden de resolución (super admin / multi-tenant):
 * 1) `tenant_id` en `localStorage` (misma prioridad que el axios del portal Next),
 * 2) `ServerContextService` (ingest / shell),
 * 3) claims del JWT,
 * 4) `vault_server_context` parseado.
 */
export function resolveTenantId(serverContext: ServerContextService): string {
  const fromCanonical = resolveVaultActiveTenantId();
  if (fromCanonical) {
    return fromCanonical;
  }

  if (typeof localStorage !== 'undefined') {
    const fromLs = normalizeTenantId(
      localStorage.getItem('tenant_id') ||
        localStorage.getItem('tenantId') ||
        localStorage.getItem('selected_tenant_id'),
    );
    if (fromLs) return fromLs;
  }

  const fromContext = tenantIdFromServerContext(serverContext);
  if (fromContext) return fromContext;

  if (typeof localStorage === 'undefined') return '';

  const tokenTenant = tenantFromAccessToken();
  if (tokenTenant) return tokenTenant;

  const fromUser = tenantFromUserData();
  if (fromUser) return fromUser;

  const fromCompany = tenantFromCompanyScope();
  if (fromCompany) return fromCompany;

  const rawContext = localStorage.getItem('vault_server_context');
  if (!rawContext) return '';
  try {
    const parsed = JSON.parse(rawContext) as { tenantId?: string };
    return normalizeTenantId(parsed.tenantId);
  } catch {
    return '';
  }
}

/**
 * Hidrata `vault_server_context` desde JWT / sesión (misma fuente que repositorio, retención, etc.).
 * No sobrescribe un tenant ya elegido en perfil o configuración de servidores.
 */
export function syncVaultTenantScopeFromSession(serverContext: ServerContextService): string {
  const existing = tenantIdFromServerContext(serverContext);
  if (existing) {
    return existing;
  }
  let tenantId = resolveTenantId(serverContext);
  if (!tenantId) {
    tenantId = tenantFromCompanyScope();
  }
  if (tenantId) {
    persistActiveTenantId(tenantId, serverContext);
  }
  return tenantId;
}

/** Desenvuelve respuestas API (array directo, `{ data }`, `{ response: { content } }`, etc.). */
export function unwrapVaultApiRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const root = raw as Record<string, unknown>;
  if (Array.isArray(root['tenants']) || root['email'] != null || root['id'] != null) {
    return root;
  }
  for (const key of ['data', 'content', 'result', 'user']) {
    const nested = root[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const record = nested as Record<string, unknown>;
      if (Array.isArray(record['tenants']) || record['email'] != null) {
        return record;
      }
    }
  }
  const response = root['response'];
  if (response && typeof response === 'object') {
    const content = (response as Record<string, unknown>)['content'];
    if (content && typeof content === 'object' && !Array.isArray(content)) {
      return content as Record<string, unknown>;
    }
  }
  return root;
}

function pickTenantIdFromLinkRow(row: Record<string, unknown>): string {
  return readVaultTenantIdFromAssignmentRow(row);
}

function tenantIdFromTenantLinks(tenants: unknown): string {
  if (!Array.isArray(tenants) || tenants.length === 0) {
    return '';
  }
  for (const entry of tenants) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const tid = pickTenantIdFromLinkRow(entry as Record<string, unknown>);
    if (tid) {
      return tid;
    }
  }
  return '';
}

function extractVaultApiArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of ['data', 'content', 'items', 'results', 'tenants']) {
    const v = o[key];
    if (Array.isArray(v)) {
      return v;
    }
  }
  const response = o['response'];
  if (response && typeof response === 'object') {
    const content = (response as Record<string, unknown>)['content'];
    if (Array.isArray(content)) {
      return content;
    }
  }
  return [];
}

/**
 * Paridad legacy `authAPI.getMyTenants()` → `GET /api/tenants/my-tenants`.
 * Fuente principal de empresa para usuarios no superadmin.
 */
export function tenantIdFromMyTenantsResponse(raw: unknown): string {
  const list = extractVaultApiArray(raw);
  if (list.length === 0) {
    return '';
  }
  if (typeof localStorage !== 'undefined') {
    const saved = (
      localStorage.getItem('tenant_id') ||
      localStorage.getItem('tenantId') ||
      ''
    ).trim();
    if (saved) {
      for (const entry of list) {
        if (!entry || typeof entry !== 'object') {
          continue;
        }
        const row = entry as Record<string, unknown>;
        const tid = pickTenantIdFromLinkRow(row);
        if (tid && tid === saved) {
          return normalizeTenantId(tid) || tid;
        }
      }
    }
  }
  if (list.length === 1) {
    const tid = pickTenantIdFromLinkRow(list[0] as Record<string, unknown>);
    if (tid) {
      return tid;
    }
  }
  return tenantIdFromTenantLinks(list);
}

/** `GET /api/auth/me` → `tenants[].tenant_id` (JWT Datavault no incluye tenant). */
export function tenantIdFromAuthMeResponse(me: unknown): string {
  const record = unwrapVaultApiRecord(me);
  if (!record) {
    return tenantIdFromMyTenantsResponse(me);
  }
  return tenantIdFromTenantLinks(record['tenants']);
}

/** Lista `/api/admin/tenants` (superadmin). Con `pickFirst`, usa la primera empresa del listado. */
export function tenantIdFromAdminTenantsList(
  raw: unknown,
  options?: { pickFirst?: boolean },
): string {
  const list = extractVaultApiArray(raw);
  if (list.length === 0) {
    return '';
  }
  if (list.length === 1 || options?.pickFirst) {
    const row = list[0] as Record<string, unknown>;
    const tid = String(row['id'] ?? row['tenant_id'] ?? row['tenantId'] ?? '').trim();
    if (tid) {
      return normalizeTenantId(tid) || tid;
    }
  }
  return '';
}

/** Tenant del usuario asignado (p. ej. cuando la sesión no trae tenant explícito). */
export function tenantIdFromAssignUsers(
  users: Array<{ id?: string; tenants?: Array<{ tenant_id?: string }> }>,
  selectedUserIds: string[],
): string {
  for (const userId of selectedUserIds) {
    const want = (userId || '').trim().toLowerCase();
    const user = users.find(
      (u) => (u.id || '').trim().toLowerCase() === want || (u.id || '').trim() === userId,
    );
    const link = user?.tenants?.find((t) => (t.tenant_id || '').trim());
    if (link?.tenant_id?.trim()) {
      return link.tenant_id.trim();
    }
  }
  const counts = new Map<string, number>();
  for (const user of users) {
    for (const link of user.tenants ?? []) {
      const tid = (link.tenant_id || '').trim();
      if (tid) {
        counts.set(tid, (counts.get(tid) ?? 0) + 1);
      }
    }
  }
  if (counts.size === 1) {
    return [...counts.keys()][0];
  }
  let best = '';
  let bestCount = 0;
  for (const [tid, count] of counts) {
    if (count > bestCount) {
      best = tid;
      bestCount = count;
    }
  }
  return best;
}

/** Tenant desde asignaciones ya existentes (`GET /api/file-assignments/assigned`). */
export function tenantIdFromFileAssignments(
  assignments: Array<{ tenant_id?: string | null }>,
): string {
  const counts = new Map<string, number>();
  for (const row of assignments) {
    const tid = (row.tenant_id || '').trim();
    if (!tid) continue;
    counts.set(tid, (counts.get(tid) ?? 0) + 1);
  }
  if (counts.size === 0) return '';
  if (counts.size === 1) return [...counts.keys()][0];
  let best = '';
  let bestCount = 0;
  for (const [tid, count] of counts) {
    if (count > bestCount) {
      best = tid;
      bestCount = count;
    }
  }
  return best;
}

/** Combina varias fuentes; devuelve el primer tenant UUID válido. */
export function coalesceTenantIds(...candidates: Array<string | null | undefined>): string {
  for (const value of candidates) {
    const tid = (value || '').trim();
    if (!tid) continue;
    const normalized = normalizeTenantId(tid);
    if (normalized) return normalized;
    if (/^[0-9a-f-]{36}$/i.test(tid)) return tid;
  }
  return '';
}

export function withTenantParam(tenantId: string, params?: HttpParams): HttpParams | undefined {
  if (!tenantId) return params;
  const base = params ?? new HttpParams();
  return base.set('tenant_id', tenantId);
}

export function tenantHeaders(tenantId: string): HttpHeaders {
  return vaultTenantHeaders(tenantId);
}
